import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Gauge, ArrowUp } from "lucide-react";
import { api } from "../api/client";
import { Button, Field, IconButton } from "../components/UIKit";
import { formatKm } from "../lib/fleet";

const PRESETS = [500, 1000, 5000, 10000];

export default function VehicleMileage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [mileage, setMileage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}`);
        setCurrent(data.mileage);
      } catch { /* ignore */ }
    })();
  }, [id]);

  const applyPreset = (delta) => {
    if (current === null) return;
    setMileage(String(current + delta));
  };

  const parsed = mileage ? parseInt(mileage) : null;
  const delta = parsed !== null && current !== null ? parsed - current : null;

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (!parsed || parsed < 0) return setError("Kilométrage invalide");
    if (current !== null && parsed < current) return setError("Le nouveau kilométrage doit être supérieur à l'actuel");
    setLoading(true);
    try {
      await api.post(`/vehicles/${id}/mileage`, { mileage: parsed });
      toast.success("Kilométrage mis à jour");
      navigate(-1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible de mettre à jour le kilométrage");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 540, margin: "0 auto", paddingBottom: 100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 12px" }}>
        <IconButton testID="mileage-back" icon={<X size={22} />} onClick={() => navigate(-1)} ariaLabel="Fermer" />
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Kilométrage</h2>
        <div style={{ width: 40 }} />
      </div>

      <form onSubmit={onSubmit} style={{ padding: "20px 24px" }}>
        <div style={{
          width: 80, height: 80, borderRadius: 24, margin: "0 auto 20px",
          background: "rgba(11,25,44,0.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Gauge size={40} color="var(--mf-brand)" />
        </div>
        <h3 style={{ margin: 0, textAlign: "center", fontSize: 22, fontWeight: 700, letterSpacing: -0.2 }}>
          Nouveau kilométrage
        </h3>
        <p style={{ margin: "6px 0 20px", textAlign: "center", fontSize: 14, color: "var(--mf-text-secondary)" }}>
          {current !== null ? `Actuel · ${formatKm(current)}` : "Les alertes seront recalculées"}
        </p>

        <Field
          testID="mileage-input"
          label="Kilomètres"
          value={mileage}
          onChange={setMileage}
          placeholder={current !== null ? String(current) : "185 000"}
          style={{ fontSize: 22, fontWeight: 700, textAlign: "center" }}
        />

        {delta !== null && delta > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
            marginBottom: 8, color: "var(--mf-ok-solid)", fontSize: 13, fontWeight: 700,
          }}>
            <ArrowUp size={14} />
            +{delta.toLocaleString("fr-FR")} km
          </div>
        )}

        <div style={{
          fontSize: 12, fontWeight: 700, color: "var(--mf-text-muted)",
          textTransform: "uppercase", letterSpacing: 0.5, marginTop: 8, marginBottom: 10,
        }}>
          Ajouter rapidement
        </div>
        <div style={{ display: "flex", gap: 8, overflowX: "auto" }} className="no-scrollbar">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              data-testid={`mileage-preset-${p}`}
              style={{
                padding: "10px 18px", borderRadius: 999,
                background: "var(--mf-bg-muted)", border: "1px solid var(--mf-border)",
                color: "var(--mf-brand)", fontWeight: 700, fontSize: 14, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              +{p.toLocaleString("fr-FR")}
            </button>
          ))}
        </div>

        {error && <div style={{ color: "var(--mf-urgent-text)", textAlign: "center", marginTop: 12, fontSize: 14 }}>{error}</div>}

        <Button testID="mileage-submit" type="submit" loading={loading} style={{ marginTop: 24 }}>
          Enregistrer
        </Button>
      </form>
    </div>
  );
}
